import React, { useState, useRef } from "react";
import { StorageService } from "../../services/storage.js";
import { VideoEmbedPlayer } from "../common/VideoEmbedPlayer.js";
import { parseVideoUrl, isVideoUrl, extractUrlFromInput } from "../../utils/media.js";
import { saveImageToUploadFolder, resolveImageUrl } from "../../utils/imageStorage.js";
import {
  Plus,
  Edit3,
  Trash2,
  Check,
  X,
  UploadCloud,
  Image as ImageIcon,
  Video as VideoIcon,
  Film,
  Star,
  Link as LinkIcon,
  Play,
  ExternalLink,
  CheckCircle2
} from "lucide-react";
export const AdminPosts = ({ posts, currentUser, onPostUpdated }) => {
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [urlType, setUrlType] = useState("image");
  const [saveSuccessMsg, setSaveSuccessMsg] = useState(null);
  const fileInputRef = useRef(null);
  const canEdit = currentUser.role === "super_admin" || currentUser.role === "principal" || currentUser.role === "editor" || currentUser.role === "teacher";
  const handleStartCreate = () => {
    setEditingPost({
      title: "",
      title_bn: "",
      category: "Academics",
      author_id: currentUser.id,
      author_name: currentUser.name,
      excerpt: "",
      excerpt_bn: "",
      content: "",
      content_bn: "",
      status: "published",
      read_time: "3 min read",
      video_url: "",
      cover_image: "/src/assets/images/madrasah_classroom_study_1790844625777.jpg",
      media: [
        {
          id: `med-${Date.now()}-1`,
          type: "image",
          url: "/src/assets/images/madrasah_classroom_study_1790844625777.jpg",
          name: "Classroom Study",
          caption: "Students practicing lessons",
          is_primary: true
        }
      ]
    });
    setIsCreating(true);
  };
  const handleStartEdit = (post) => {
    const initialMedia = post.media && post.media.length > 0 ? [...post.media] : post.cover_image ? [{ id: `med-${Date.now()}`, type: "image", url: post.cover_image, name: "Cover Image", is_primary: true }] : [];
    if (post.video_url && !initialMedia.some((m) => m.url === post.video_url)) {
      initialMedia.push({
        id: `med-vid-${Date.now()}`,
        type: "video",
        url: post.video_url,
        name: "Featured Video",
        is_primary: false
      });
    }
    setEditingPost({
      ...post,
      media: initialMedia
    });
    setIsCreating(false);
  };
  const handleVideoUrlChange = (rawVal) => {
    const cleanVal = extractUrlFromInput(rawVal);
    const parsed = cleanVal ? parseVideoUrl(cleanVal) : null;
    setEditingPost((prev) => {
      if (!prev) return prev;
      let mediaList = prev.media ? [...prev.media] : [];
      if (!cleanVal) {
        return {
          ...prev,
          video_url: ""
        };
      }
      let cover = prev.cover_image;
      if (!cover && parsed?.thumbnailUrl) {
        cover = parsed.thumbnailUrl;
      }
      const existingIdx = mediaList.findIndex((m) => m.type === "video");
      const videoMediaItem = {
        id: existingIdx !== -1 ? mediaList[existingIdx].id : `med-vid-${Date.now()}`,
        type: "video",
        url: cleanVal,
        name: parsed?.platformTitle || "Video",
        caption: existingIdx !== -1 ? mediaList[existingIdx].caption : "",
        is_primary: false
      };
      if (existingIdx !== -1) {
        mediaList[existingIdx] = videoMediaItem;
      } else {
        mediaList.push(videoMediaItem);
      }
      return {
        ...prev,
        video_url: cleanVal,
        cover_image: cover,
        media: mediaList
      };
    });
  };
  const handleSave = (e) => {
    e.preventDefault();
    if (!editingPost || !editingPost.title) return;
    const finalPost = { ...editingPost };
    if (finalPost.video_url && (!finalPost.media || !finalPost.media.some((m) => m.url === finalPost.video_url))) {
      finalPost.media = [
        ...finalPost.media || [],
        {
          id: `med-vid-${Date.now()}`,
          type: "video",
          url: finalPost.video_url,
          name: parseVideoUrl(finalPost.video_url).platformTitle,
          is_primary: false
        }
      ];
    }
    StorageService.savePost(finalPost);
    setSaveSuccessMsg(`Article "${finalPost.title}" saved successfully to MySQL database!`);
    setTimeout(() => setSaveSuccessMsg(null), 3500);
    setEditingPost(null);
    setIsCreating(false);
    onPostUpdated();
  };
  const handleDelete = (id) => {
    StorageService.deletePost(id);
    setDeleteConfirmId(null);
    onPostUpdated();
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  };
  const processFiles = (files) => {
    const fileArray = Array.from(files);
    fileArray.forEach((file) => {
      const isVideo = file.type.startsWith("video");
      const isImage = file.type.startsWith("image");
      if (!isVideo && !isImage) return;
      const cleanPath = saveImageToUploadFolder(file, "blog");
      const newMediaItem = {
        id: `med-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        type: isVideo ? "video" : "image",
        url: cleanPath,
        name: file.name,
        caption: "",
        is_primary: false
      };
      setEditingPost((prev) => {
        if (!prev) return prev;
        const currentMedia = prev.media || [];
        const hasImage = currentMedia.some((m) => m.type === "image" && m.is_primary);
        if (!hasImage && !isVideo) {
          newMediaItem.is_primary = true;
        }
        const updatedMedia = [...currentMedia, newMediaItem];
        const primaryImg = updatedMedia.find((m) => m.is_primary && m.type === "image") || updatedMedia.find((m) => m.type === "image");
        return {
          ...prev,
          media: updatedMedia,
          cover_image: primaryImg ? primaryImg.url : prev.cover_image
        };
      });
    });
  };
  const handleAddUrlMedia = () => {
    if (!urlInput.trim()) return;
    const cleanInput = extractUrlFromInput(urlInput.trim());
    const isDetectedVideo = urlType === "video" || isVideoUrl(cleanInput);
    const videoData = isDetectedVideo ? parseVideoUrl(cleanInput) : null;
    let mediaName = "Media Attachment";
    if (isDetectedVideo) {
      if (videoData?.platform === "youtube") {
        mediaName = "YouTube Video";
      } else if (videoData?.platform === "facebook") {
        mediaName = "Facebook Video";
      } else if (videoData?.platform === "vimeo") {
        mediaName = "Vimeo Video";
      } else {
        mediaName = "Video Stream";
      }
    } else {
      mediaName = "Photo Attachment";
    }
    const newMediaItem = {
      id: `med-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      type: isDetectedVideo ? "video" : "image",
      url: cleanInput,
      name: mediaName,
      caption: "",
      is_primary: false
    };
    setEditingPost((prev) => {
      if (!prev) return prev;
      const currentMedia = prev.media || [];
      const hasImage = currentMedia.some((m) => m.type === "image" && m.is_primary);
      if (!hasImage && !isDetectedVideo) {
        newMediaItem.is_primary = true;
      }
      let cover = prev.cover_image;
      if (!cover && videoData?.thumbnailUrl) {
        cover = videoData.thumbnailUrl;
      }
      const updatedMedia = [...currentMedia, newMediaItem];
      const primaryImg = updatedMedia.find((m) => m.is_primary && m.type === "image") || updatedMedia.find((m) => m.type === "image");
      return {
        ...prev,
        media: updatedMedia,
        video_url: isDetectedVideo ? prev.video_url || cleanInput : prev.video_url,
        cover_image: primaryImg ? primaryImg.url : cover || prev.cover_image
      };
    });
    setUrlInput("");
  };
  const handleSetPrimaryMedia = (mediaId) => {
    setEditingPost((prev) => {
      if (!prev) return prev;
      const currentMedia = (prev.media || []).map((m) => ({
        ...m,
        is_primary: m.id === mediaId
      }));
      const selected = currentMedia.find((m) => m.id === mediaId);
      return {
        ...prev,
        media: currentMedia,
        cover_image: selected?.type === "image" ? selected.url : prev.cover_image
      };
    });
  };
  const handleRemoveMedia = (mediaId) => {
    setEditingPost((prev) => {
      if (!prev) return prev;
      const targetMedia = (prev.media || []).find((m) => m.id === mediaId);
      const updatedMedia = (prev.media || []).filter((m) => m.id !== mediaId);
      if (updatedMedia.length > 0 && !updatedMedia.some((m) => m.is_primary)) {
        const firstImg = updatedMedia.find((m) => m.type === "image");
        if (firstImg) firstImg.is_primary = true;
      }
      const primary = updatedMedia.find((m) => m.is_primary && m.type === "image") || updatedMedia.find((m) => m.type === "image");
      const isVideoMatching = targetMedia?.url === prev.video_url;
      return {
        ...prev,
        video_url: isVideoMatching ? "" : prev.video_url,
        media: updatedMedia,
        cover_image: primary ? primary.url : updatedMedia.length === 0 ? "" : prev.cover_image
      };
    });
  };
  const handleUpdateCaption = (mediaId, caption) => {
    setEditingPost((prev) => {
      if (!prev) return prev;
      const currentMedia = (prev.media || []).map(
        (m) => m.id === mediaId ? { ...m, caption } : m
      );
      return { ...prev, media: currentMedia };
    });
  };
  const currentVideoInfo = editingPost?.video_url ? parseVideoUrl(editingPost.video_url) : null;
  return <div className="space-y-6">
      {
    /* Top Header */
  }
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-serif font-bold text-slate-900 tracking-tight">
            Blog & Article Manager
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Create, moderate, and publish madrasah news, articles, and curriculum updates with YouTube & Facebook videos and photo galleries.
          </p>
        </div>

        {canEdit && <button
    onClick={handleStartCreate}
    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-800 text-white rounded-xl text-xs font-semibold hover:bg-emerald-900 shadow-sm cursor-pointer transition-colors"
  >
            <Plus className="w-4 h-4" />
            <span>Create New Article</span>
          </button>}
      </div>

      {
    /* Action Notification */
  }
      {saveSuccessMsg && <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{saveSuccessMsg}</span>
        </div>}

      {
    /* Video Preview Modal (for testing player) */
  }
      {previewVideoUrl && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-3xl w-full p-5 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between text-white border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <VideoIcon className="w-5 h-5 text-amber-400" />
                <h3 className="font-bold text-sm">Video Playback Test</h3>
              </div>
              <button
    onClick={() => setPreviewVideoUrl(null)}
    className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 cursor-pointer"
  >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="aspect-video w-full rounded-xl overflow-hidden bg-black">
              <VideoEmbedPlayer url={previewVideoUrl} autoPlay={true} />
            </div>

            <div className="flex items-center justify-between text-xs text-slate-400 pt-1">
              <span className="font-mono truncate max-w-md">{previewVideoUrl}</span>
              <a
    href={previewVideoUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="text-amber-400 hover:underline inline-flex items-center gap-1 font-semibold"
  >
                <span>Open in separate tab</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>}

      {
    /* Article Create / Edit Modal */
  }
      {editingPost && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-stone-200 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-stone-200 pb-4">
              <div>
                <h3 className="text-lg font-serif font-bold text-slate-900">
                  {isCreating ? "Create New Article & Media" : "Edit Article & Media"}
                </h3>
                <p className="text-xs text-slate-500">
                  Fill in article details, attach multiple photos via drag & drop, and add YouTube or Facebook videos.
                </p>
              </div>
              <button
    onClick={() => setEditingPost(null)}
    className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-stone-100 cursor-pointer"
  >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-6">
              {
    /* Category, Read Time, and Status */
  }
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Category
                  </label>
                  <select
    value={editingPost.category || "Academics"}
    onChange={(e) => setEditingPost({ ...editingPost, category: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 bg-white"
  >
                    <option value="Academics">Academics</option>
                    <option value="Islamic Guidance">Islamic Guidance</option>
                    <option value="Events">Events</option>
                    <option value="Admissions">Admissions</option>
                    <option value="Achievements">Achievements</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Publication Status
                  </label>
                  <select
    value={editingPost.status || "published"}
    onChange={(e) => setEditingPost({ ...editingPost, status: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 bg-white"
  >
                    <option value="published">Published (Live on Public Site)</option>
                    <option value="draft">Draft (Admin Only)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Read Time
                  </label>
                  <input
    type="text"
    value={editingPost.read_time || "3 min read"}
    onChange={(e) => setEditingPost({ ...editingPost, read_time: e.target.value })}
    placeholder="e.g. 4 min read"
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300"
  >
                  </input>
                </div>
              </div>

              {
    /* Bilingual Titles */
  }
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Article Title (English) *
                  </label>
                  <input
    type="text"
    required
    value={editingPost.title || ""}
    onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
    placeholder="e.g. Annual Hifz Convocation 2026 Celebrated"
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300"
  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    শিরোনাম (বাংলা) *
                  </label>
                  <input
    type="text"
    required
    value={editingPost.title_bn || ""}
    onChange={(e) => setEditingPost({ ...editingPost, title_bn: e.target.value })}
    placeholder="নিবন্ধের শিরোনাম বাংলায় লিখুন..."
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 font-bengali"
  />
                </div>
              </div>

              {
    /* ---------------------------------------------------- */
  }
              {
    /* DEDICATED FEATURED VIDEO URL (YOUTUBE / FACEBOOK)     */
  }
              {
    /* ---------------------------------------------------- */
  }
              <div className="p-4 bg-slate-900 text-white rounded-2xl border border-slate-800 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <VideoIcon className="w-5 h-5 text-amber-400" />
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-200">
                      Featured Video URL (YouTube, Facebook, or MP4) / ভিডিও লিংক
                    </label>
                  </div>
                  {currentVideoInfo?.isValid && <div className="flex items-center gap-2">
                      <span
    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${currentVideoInfo.platform === "youtube" ? "bg-red-600 text-white" : currentVideoInfo.platform === "facebook" ? "bg-blue-600 text-white" : "bg-emerald-600 text-white"}`}
  >
                        <Check className="w-3 h-3" />
                        <span>{currentVideoInfo.platformTitle} Detected</span>
                      </span>
                      <button
    type="button"
    onClick={() => setPreviewVideoUrl(editingPost.video_url || "")}
    className="px-2.5 py-1 text-[11px] font-semibold bg-slate-800 hover:bg-slate-700 text-amber-300 rounded-lg flex items-center gap-1 cursor-pointer transition-colors border border-slate-700"
  >
                        <Play className="w-3 h-3 fill-current" />
                        <span>Test Play Video</span>
                      </button>
                    </div>}
                </div>

                <div className="relative">
                  <LinkIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
    type="text"
    value={editingPost.video_url || ""}
    onChange={(e) => handleVideoUrlChange(e.target.value)}
    placeholder="Paste YouTube link (https://www.youtube.com/watch?v=... or https://youtu.be/...) or Facebook video link (https://fb.watch/... or https://facebook.com/...)"
    className="w-full pl-9 pr-24 py-2 text-xs rounded-xl bg-slate-950 border border-slate-700 text-white font-mono placeholder:text-slate-500 focus:border-amber-400 focus:outline-hidden"
  />
                  {editingPost.video_url && <button
    type="button"
    onClick={() => handleVideoUrlChange("")}
    className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-[10px] font-bold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded cursor-pointer"
  >
                      Clear
                    </button>}
                </div>

                {
    /* Quick Presets for Madrasah YouTube & Facebook Testing */
  }
                <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px] text-slate-400">
                  <span className="text-slate-500 font-medium">Quick Sample Links:</span>
                  <button
    type="button"
    onClick={() => handleVideoUrlChange("https://www.youtube.com/watch?v=3g_v3P9z8y0")}
    className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-red-300 border border-slate-700 cursor-pointer flex items-center gap-1"
  >
                    <span>▶ Sample YouTube (Quran Recitation)</span>
                  </button>
                  <button
    type="button"
    onClick={() => handleVideoUrlChange("https://www.facebook.com/facebook/videos/10153231379946729/")}
    className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-blue-300 border border-slate-700 cursor-pointer flex items-center gap-1"
  >
                    <span>▶ Sample Facebook Video</span>
                  </button>
                </div>

                {
    /* Live Preview Box when valid video entered */
  }
                {editingPost.video_url && currentVideoInfo?.isValid && <div className="pt-2">
                    <div className="p-3 bg-black/60 rounded-xl border border-slate-800 flex flex-col sm:flex-row items-center gap-3">
                      {currentVideoInfo.thumbnailUrl ? <div className="relative w-32 h-20 rounded-lg overflow-hidden shrink-0 bg-slate-800 border border-slate-700">
                          <img
    src={currentVideoInfo.thumbnailUrl}
    alt="Video Thumbnail"
    className="w-full h-full object-cover"
  />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <Play className="w-5 h-5 text-white fill-white" />
                          </div>
                        </div> : <div className="w-32 h-20 rounded-lg bg-blue-950 flex flex-col items-center justify-center text-blue-300 shrink-0 border border-blue-800">
                          <Film className="w-6 h-6 mb-1" />
                          <span className="text-[10px] font-bold">Facebook Video</span>
                        </div>}

                      <div className="flex-1 space-y-1 text-xs">
                        <div className="font-bold text-white flex items-center gap-2">
                          <span>{currentVideoInfo.platformTitle} Connected</span>
                          <span className="text-[10px] text-emerald-400 bg-emerald-950/80 px-1.5 py-0.5 rounded border border-emerald-800">
                            Ready for Public Embed
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 line-clamp-1 font-mono">
                          {currentVideoInfo.cleanUrl}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          This video will be displayed in the interactive reader modal on the public Blog and Article section.
                        </p>
                      </div>

                      <button
    type="button"
    onClick={() => setPreviewVideoUrl(editingPost.video_url || "")}
    className="px-3 py-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg flex items-center gap-1 cursor-pointer transition-colors shrink-0"
  >
                        <Play className="w-3.5 h-3.5 fill-current" />
                        <span>Preview Now</span>
                      </button>
                    </div>
                  </div>}
              </div>

              {
    /* ---------------------------------------------------- */
  }
              {
    /* DRAG AND DROP MULTIPLE PHOTO & VIDEO UPLOAD SECTION */
  }
              {
    /* ---------------------------------------------------- */
  }
              <div className="space-y-4 p-5 bg-stone-50 rounded-2xl border border-stone-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                      <UploadCloud className="w-4 h-4 text-emerald-700" />
                      <span>Upload Photos & Media Attachments (Multiple Drag & Drop)</span>
                    </h3>
                    <p className="text-[11px] text-slate-500">
                      Drag files from your desktop or select multiple files. Supports images (.jpg, .png, .webp) and videos (.mp4, .webm).
                    </p>
                  </div>
                  <span className="text-[11px] font-mono text-emerald-800 font-bold bg-emerald-100/80 px-2 py-0.5 rounded">
                    {(editingPost.media || []).length} items attached
                  </span>
                </div>

                {
    /* Dropzone Area */
  }
                <div
    onDragOver={handleDragOver}
    onDragLeave={handleDragLeave}
    onDrop={handleDrop}
    onClick={() => fileInputRef.current?.click()}
    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${isDragging ? "border-emerald-600 bg-emerald-50/80 scale-[1.01]" : "border-stone-300 hover:border-emerald-700 bg-white"}`}
  >
                  <input
    type="file"
    multiple
    accept="image/*,video/*"
    ref={fileInputRef}
    onChange={handleFileInputChange}
    className="hidden"
  />

                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-800 flex items-center justify-center">
                      <UploadCloud className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="font-bold text-xs text-slate-800 block">
                        Drop photos or videos here, or <span className="text-emerald-800 underline">browse files</span>
                      </span>
                      <span className="text-[11px] text-slate-400">
                        Select multiple photos/videos at once · Immediate local preview and MySQL storage
                      </span>
                    </div>
                  </div>
                </div>

                {
    /* Alternative: Add by Direct URL or Embed */
  }
                <div className="pt-2 flex flex-col sm:flex-row items-center gap-2">
                  <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-stone-200">
                    <button
    type="button"
    onClick={() => setUrlType("image")}
    className={`px-2.5 py-1 text-[11px] font-semibold rounded cursor-pointer ${urlType === "image" ? "bg-emerald-900 text-white" : "text-slate-600"}`}
  >
                      Photo URL
                    </button>
                    <button
    type="button"
    onClick={() => setUrlType("video")}
    className={`px-2.5 py-1 text-[11px] font-semibold rounded cursor-pointer ${urlType === "video" ? "bg-emerald-900 text-white" : "text-slate-600"}`}
  >
                      Video URL
                    </button>
                  </div>

                  <div className="relative flex-1 w-full">
                    <LinkIcon className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
    type="text"
    value={urlInput}
    onChange={(e) => setUrlInput(e.target.value)}
    placeholder={urlType === "image" ? "Paste image URL (https://...)" : "Paste YouTube (https://youtu.be/...), Facebook video (https://fb.watch/...), or MP4 URL"}
    className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-stone-200 bg-white font-mono"
  />
                  </div>

                  <button
    type="button"
    onClick={handleAddUrlMedia}
    className="w-full sm:w-auto px-4 py-1.5 text-xs font-semibold text-slate-800 bg-white hover:bg-stone-100 border border-stone-200 rounded-lg cursor-pointer"
  >
                    Add to Gallery
                  </button>
                </div>

                {
    /* Attached Media Grid */
  }
                {(editingPost.media || []).length > 0 && <div className="pt-4 border-t border-stone-200 space-y-2">
                    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block">
                      Attached Media Tray ({editingPost.media?.length} Items)
                    </span>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {(editingPost.media || []).map((med) => {
    const isVid = med.type === "video";
    const vInfo = isVid ? parseVideoUrl(med.url) : null;
    const isPrimary = med.is_primary;
    return <div
      key={med.id}
      className={`p-2 rounded-xl bg-white border space-y-2 relative transition-all ${isPrimary ? "border-amber-400 ring-2 ring-amber-300/40 shadow-xs" : "border-stone-200"}`}
    >
                            <div className="relative aspect-video rounded-lg overflow-hidden bg-slate-900 flex items-center justify-center">
                              {isVid ? vInfo?.thumbnailUrl ? <img
      src={vInfo.thumbnailUrl}
      alt="Video thumbnail"
      className="w-full h-full object-cover"
    /> : <div className="flex flex-col items-center justify-center text-white">
                                    <Film className="w-6 h-6 text-amber-400 mb-1" />
                                    <span className="text-[10px] font-mono">{vInfo?.platform || "Video"}</span>
                                  </div> : <img
      src={resolveImageUrl(med.url)}
      alt={med.name || "Post attachment"}
      className="w-full h-full object-cover"
      referrerPolicy="no-referrer"
      onError={(e) => {
        e.currentTarget.src = "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg";
      }}
    />}

                              {
      /* Badges */
    }
                              <div className="absolute top-1.5 left-1.5 flex items-center gap-1 z-10 pointer-events-none">
                                <span
      className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${isVid ? vInfo?.platform === "youtube" ? "bg-red-700 text-white" : vInfo?.platform === "facebook" ? "bg-blue-700 text-white" : "bg-purple-900 text-white" : "bg-emerald-950 text-white"}`}
    >
                                  {isVid ? vInfo?.platform || "video" : "image"}
                                </span>
                                {isPrimary && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-400 text-emerald-950 flex items-center gap-0.5">
                                    <Star className="w-2.5 h-2.5 fill-current" />
                                    <span>Cover</span>
                                  </span>}
                              </div>

                              <button
      type="button"
      onClick={() => handleRemoveMedia(med.id)}
      className="absolute top-1.5 right-1.5 p-1 rounded bg-black/60 hover:bg-red-600 text-white transition-colors cursor-pointer z-10"
      title="Remove media"
    >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            {
      /* Caption Input & Test Play */
    }
                            <div className="flex items-center gap-1">
                              <input
      type="text"
      value={med.caption || ""}
      onChange={(e) => handleUpdateCaption(med.id, e.target.value)}
      placeholder="Caption / credit..."
      className="flex-1 px-2 py-1 text-[11px] rounded border border-stone-200 bg-stone-50 focus:bg-white"
    />
                              {isVid && <button
      type="button"
      onClick={() => setPreviewVideoUrl(med.url)}
      className="p-1 text-slate-500 hover:text-emerald-700 bg-stone-100 rounded cursor-pointer"
      title="Test play video"
    >
                                  <Play className="w-3 h-3 fill-current" />
                                </button>}
                            </div>

                            {
      /* Set as Cover Action */
    }
                            {med.type === "image" && !isPrimary && <button
      type="button"
      onClick={() => handleSetPrimaryMedia(med.id)}
      className="w-full py-1 text-[10px] font-bold text-amber-900 bg-amber-50 hover:bg-amber-100 rounded border border-amber-200 flex items-center justify-center gap-1 cursor-pointer transition-colors"
    >
                                <Star className="w-3 h-3" />
                                <span>Set as Primary Cover</span>
                              </button>}
                          </div>;
  })}
                    </div>
                  </div>}
              </div>

              {
    /* Excerpts */
  }
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Brief Excerpt (English)
                  </label>
                  <textarea
    rows={2}
    value={editingPost.excerpt || ""}
    onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
    placeholder="Short summary for preview cards..."
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300"
  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    সংক্ষিপ্ত সারসংক্ষেপ (বাংলা)
                  </label>
                  <textarea
    rows={2}
    value={editingPost.excerpt_bn || ""}
    onChange={(e) => setEditingPost({ ...editingPost, excerpt_bn: e.target.value })}
    placeholder="প্রদর্শনীর জন্য সংক্ষেপ সারসংক্ষেপ..."
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 font-bengali"
  />
                </div>
              </div>

              {
    /* Full Content */
  }
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Full Content (English)
                  </label>
                  <textarea
    rows={7}
    value={editingPost.content || ""}
    onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
    placeholder="Write detailed article text in English..."
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 font-sans"
  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    পূর্ণাঙ্গ বিষয়বস্তু (বাংলা)
                  </label>
                  <textarea
    rows={7}
    value={editingPost.content_bn || ""}
    onChange={(e) => setEditingPost({ ...editingPost, content_bn: e.target.value })}
    placeholder="পূর্ণাঙ্গ প্রবন্ধ বাংলায় লিখুন..."
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 font-bengali"
  />
                </div>
              </div>

              {
    /* Action Buttons */
  }
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-stone-200">
                <button
    type="button"
    onClick={() => setEditingPost(null)}
    className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-stone-100 rounded-lg cursor-pointer"
  >
                  Cancel
                </button>
                <button
    type="submit"
    className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-lg shadow-sm cursor-pointer"
  >
                  Save Post & Media to MySQL
                </button>
              </div>
            </form>
          </div>
        </div>}

      {
    /* Delete Confirmation Modal */
  }
      {deleteConfirmId && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="bg-white max-w-sm w-full p-6 rounded-2xl shadow-xl border border-stone-200 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-700 flex items-center justify-center mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-slate-900 text-base">Delete this article?</h3>
            <p className="text-xs text-slate-500">
              This will remove the article and all attached photo/video records from the MySQL database table.
            </p>
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
    onClick={() => setDeleteConfirmId(null)}
    className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-stone-100 rounded-lg cursor-pointer"
  >
                Cancel
              </button>
              <button
    onClick={() => handleDelete(deleteConfirmId)}
    className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm cursor-pointer"
  >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>}

      {
    /* Posts Table */
  }
      <div className="bg-white rounded-2xl border border-stone-200 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-stone-50 border-b border-stone-200 text-slate-600">
              <tr>
                <th className="py-3 px-4 font-semibold">Article & Media</th>
                <th className="py-3 px-4 font-semibold">Category</th>
                <th className="py-3 px-4 font-semibold">Media Count</th>
                <th className="py-3 px-4 font-semibold">Author</th>
                <th className="py-3 px-4 font-semibold">Status</th>
                <th className="py-3 px-4 font-semibold">Views</th>
                <th className="py-3 px-4 font-semibold">Created</th>
                <th className="py-3 px-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {posts.map((post) => {
    const mediaCount = (post.media || []).length || (post.cover_image ? 1 : 0);
    const hasVideo = Boolean(post.video_url) || (post.media || []).some((m) => m.type === "video");
    const vInfo = post.video_url ? parseVideoUrl(post.video_url) : null;
    return <tr key={post.id} className="hover:bg-stone-50/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-slate-900 max-w-xs">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-stone-100 shrink-0 border border-stone-200 relative">
                          {post.cover_image ? <img
      src={resolveImageUrl(post.cover_image)}
      alt=""
      className="w-full h-full object-cover"
      referrerPolicy="no-referrer"
      onError={(e) => {
        e.currentTarget.src = "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg";
      }}
    /> : <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <ImageIcon className="w-4 h-4" />
                            </div>}
                          {hasVideo && <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <Play className="w-3.5 h-3.5 text-white fill-white" />
                            </div>}
                        </div>
                        <div className="truncate">
                          <div className="truncate">{post.title}</div>
                          <div className="truncate text-[11px] text-slate-500 font-normal font-bengali">
                            {post.title_bn}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      <span className="bg-stone-100 text-slate-700 px-2 py-0.5 rounded text-[11px] font-medium">
                        {post.category}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5 text-xs text-slate-600 font-mono">
                        <ImageIcon className="w-3.5 h-3.5 text-emerald-700" />
                        <span>{mediaCount} item{mediaCount !== 1 ? "s" : ""}</span>
                        {hasVideo && <span
      className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ml-1 ${vInfo?.platform === "youtube" ? "bg-red-100 text-red-700" : vInfo?.platform === "facebook" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}
      title={`Video: ${post.video_url}`}
    >
                            {vInfo?.platform === "youtube" ? "YouTube" : vInfo?.platform === "facebook" ? "Facebook" : "Video"}
                          </span>}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{post.author_name}</td>
                    <td className="py-3 px-4">
                      <span
      className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded uppercase ${post.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-slate-600"}`}
    >
                        {post.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono tabular-nums text-slate-600">
                      {post.views}
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-500 tabular-nums">
                      {post.created_at.substring(0, 10)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {post.video_url && <button
      type="button"
      onClick={() => setPreviewVideoUrl(post.video_url || "")}
      className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg cursor-pointer"
      title="Play video"
    >
                            <Play className="w-3.5 h-3.5 fill-current" />
                          </button>}
                        {canEdit && <button
      onClick={() => handleStartEdit(post)}
      className="p-1.5 text-slate-500 hover:text-emerald-800 hover:bg-stone-100 rounded-lg cursor-pointer"
      title="Edit article and media"
    >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>}
                        {currentUser.role === "super_admin" && <button
      onClick={() => setDeleteConfirmId(post.id)}
      className="p-1.5 text-slate-500 hover:text-red-700 hover:bg-red-50 rounded-lg cursor-pointer"
      title="Delete article"
    >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>}
                      </div>
                    </td>
                  </tr>;
  })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};
